import React, { Component, ReactNode } from 'react';
import './ReportsList.scss';
import { DocumentsTable } from '../../features/documents-table/DocumentsTable';
import { Document, DocumentInterface, EDocumentType } from '../../models/Document';
import { FileUpload } from '../../features/file-upload/FileUpload';
import { DocumentResponse } from '../../api/types';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import { Api } from '../../api';
import { connect } from 'react-redux';
import { ApplicationState } from '../../app/store';
import { IDossierV2 } from '../../models/DossierV2';
import { DossierMemberInterface } from '../../models/DossierMember';

export enum EReportType {
  PRIVATE = 'private',
  PRIVATE_CLIENT = 'private-client',
  SHARED = 'shared',
}

interface IReportsListProps {
  dossierId: number;
  dossier?: IDossierV2;
  dossierRelation?: DossierMemberInterface;
  source: EReportType;
  isCaregiverOrAdmin: boolean;
}

interface IReportsListState {
  documents: Document[];
  loading: boolean;
  loadingError?: string;
}

class ReportsList extends Component<IReportsListProps, IReportsListState> {
  state: IReportsListState = {
    documents: [],
    loading: true,
  };

  componentDidMount(): void {
    this.setState({ loading: true }, () => this.loadDocuments());
  }

  componentDidUpdate(prevProps: IReportsListProps): void {
    if (
      this.props.dossierId !== prevProps.dossierId ||
      this.props.source !== prevProps.source
    ) {
      this.setState({ documents: [], loading: true, loadingError: undefined }, () => this.loadDocuments());
    }
  }

  private getLoadDocumentsApiCall(source: EReportType, dossierId: number): Promise<DocumentInterface[]> {
    switch (source) {
      case EReportType.PRIVATE:
        return Api.listDossierDocuments(dossierId);
      case EReportType.PRIVATE_CLIENT:
        return Api.listClientDossierDocuments(dossierId);
      case EReportType.SHARED:
      default:
        return Api.listSharedDossierDocuments(dossierId);
    }
  }

  private getDownloadDocumentsApiCall(source: EReportType, dossierId: number, document: Document): Promise<Blob> {
    switch (source) {
      case EReportType.PRIVATE:
        return Api.getDossierDocument(dossierId, document);
      case EReportType.PRIVATE_CLIENT:
        return Api.getClientDossierDocument(dossierId, document);
      case EReportType.SHARED:
      default:
        return Api.getSharedDossierDocument(dossierId, document);
    }
  }

  private getDeleteDocumentsApiCall(source: EReportType, dossierId: number, document: Document): Promise<void> {
    switch (source) {
      case EReportType.PRIVATE:
        return Api.deleteDossierDocument(dossierId, document);
      case EReportType.PRIVATE_CLIENT:
        return Api.deleteClientDossierDocument(dossierId, document);
      case EReportType.SHARED:
      default:
        return Api.deleteSharedDossierDocument(dossierId, document);
    }
  }

  private getUploadDocumentsApiCall(source: EReportType, dossierId: number, formData: FormData): Promise<DocumentInterface> {
    switch (source) {
      case EReportType.PRIVATE:
        return Api.uploadDossierDocument(dossierId, formData);
      case EReportType.PRIVATE_CLIENT:
        return Api.uploadClientDossierDocument(dossierId, formData);
      case EReportType.SHARED:
      default:
        return Api.uploadSharedDossierDocument(dossierId, formData);
    }
  }

  loadDocuments(): void {
    const { dossierId, source } = this.props;
    if (dossierId) {
      const apiCall = this.getLoadDocumentsApiCall(source, dossierId);
      apiCall
        .then(response => this.setState({ documents: Document.fromList(response), loading: false }))
        .catch(err => {
          console.error('Loading dossier documents failed: ', err);
          this.setState({ loadingError: 'Ophalen van dossier mislukt, probeer het later opnieuw', loading: false });
        });
    } else {
      this.setState({ loading: false, loadingError: 'Kan documenten van dossier niet ophalen omdat er geen dossier geselecteerd is' });
    }
  }

  handleDownloadDocument = (document: Document): Promise<Blob> => {
    const { dossierId, source } = this.props;
    if (dossierId) {
      const apiCall = this.getDownloadDocumentsApiCall(source, dossierId, document);
      return apiCall
        .catch(err => {
          NotificationHelper.toastError('Openen bestand mislukt, probeer het later opnieuw.');
          throw err;
        });
    } else {
      NotificationHelper.toastError('Openen bestand mislukt, omdat er geen dossier geselecteerd is.');
      return Promise.reject(new Error('Dossier not selected'));
    }
  };

  handleDeleteDocument = (deleteDocument: Document): Promise<void> => {
    const { dossierId, source } = this.props;
    if (dossierId) {
      const apiCall = this.getDeleteDocumentsApiCall(source, dossierId, deleteDocument);
      return apiCall
        .then(() => {
          const newDocumentsList: Document[] = [];
          this.state.documents.forEach(document => {
            if (!document.match(deleteDocument)) {
              newDocumentsList.push(document);
            }
          });
          this.setState({ documents: newDocumentsList });
        });
    } else {
      // How did you even end up in this state? But whatever here's an error for ya.
      NotificationHelper.toastError('Verwijderen bestand mislukt, omdat er geen dossier geselecteerd is.');
      return Promise.reject(new Error('Missing dossier id'));
    }
  };

  handleUpload = (formData: FormData): Promise<DocumentResponse> => {
    const { dossierId, source } = this.props;
    if (dossierId) {
      const apiCall = this.getUploadDocumentsApiCall(source, dossierId, formData);
      return apiCall
        .catch(err => {
          if (err.key === 'InvalidFileTypeError') {
            NotificationHelper.toastError('Uploaden mislukt: Bestandstype niet toegestaan.');
          } else {
            NotificationHelper.toastError('Uploaden bestand mislukt door een onverwachte fout.');
          }
          throw err;
        });
    } else {
      return Promise.reject(new Error('Missing dossier id'));
    }
  };

  handleUploaded = (): void => this.loadDocuments();

  private isCaregiverOrAdmin(): boolean {
    return this.props.isCaregiverOrAdmin;
  }

  private isDeleteAllowed(): boolean {
    const { dossier } = this.props;
    return !dossier?.archived && this.isCaregiverOrAdmin();
  }

  private isUploadAllowed(): boolean {
    const { dossier } = this.props;
    return !dossier?.archived;
  }

  render(): ReactNode {
    const { documents, loading, loadingError } = this.state;
    const isEmpty: boolean = (!loading && !loadingError && documents.length === 0);
    const allowDelete = this.isDeleteAllowed();
    const allowUpload = this.isUploadAllowed();
    const documentType = this.props.source === EReportType.SHARED ? EDocumentType.SHARED : EDocumentType.FILE;
    return (
      <div className="client-file__container">
        <div className="client-file__items">
          { loadingError && <Alert className="reports-list__danger" variant="danger">{loadingError}</Alert> }
          { isEmpty && <Alert className="reports-list__info" variant="info">Er zijn nog geen verslagen</Alert> }
          {
            loading ?
              <Spinner animation="border" variant="primary" /> :
              <>
                { !isEmpty && <DocumentsTable documents={documents} documentsType={documentType} allowDelete={allowDelete} onDeleteDocument={this.handleDeleteDocument} onDownloadDocument={this.handleDownloadDocument} /> }
                { allowUpload && <FileUpload className="client-file__file-upload" onUpload={this.handleUpload} onUploaded={this.handleUploaded} /> }
              </>
          }
        </div>
      </div>
    );
  }
}

export default connect((state: ApplicationState) => ({
  dossier: state.session.dossier,
  dossierRelation: state.session.relation,
}))(ReportsList);
